'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const SESSION_ALIVE_KEY = 'mrp_tab_alive';

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [currentCompany, setCurrentCompany] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  const supabase = createClient();

  useEffect(() => {
    // ── Session-on-close guard ──────────────────────────────────────────────
    // If the user closed the browser/tab, sessionStorage is wiped.
    // On the next page load the flag won't be present, so we sign them out
    // to force a fresh login.
    const tabAlive = typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(SESSION_ALIVE_KEY)
      : null;

    const initAuth = async () => {
      if (!tabAlive) {
        // New browser session — sign out any persisted cookie/localStorage session
        await supabase.auth.signOut();
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem(SESSION_ALIVE_KEY, '1');
        }
        setLoading(false);
        return;
      }

      // Tab was already alive (page refresh within same browser session)
      supabase.auth.getSession().then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          loadUserContext(session.user.id);
        } else {
          setLoading(false);
        }
      });
    };

    // Mark tab as alive for this browser session
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(SESSION_ALIVE_KEY, '1');
    }

    initAuth();

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserContext(session.user.id);
      } else {
        setCurrentCompany(null);
        setUserProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadUserContext = async (userId: string) => {
    try {
      // Load user profile
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      setUserProfile(profile);

      // Load user's company memberships
      if (profile) {
        const { data: memberships } = await supabase
          .from('user_company_memberships')
          .select('*, companies(*)')
          .eq('user_id', userId)
          .eq('status', 'active')
          .order('created_at', { ascending: true });

        if (memberships && memberships.length > 0) {
          // Check localStorage for last selected company
          const savedCompanyId = typeof window !== 'undefined'
            ? localStorage.getItem(`mrp_active_company_${userId}`)
            : null;

          const savedMembership = savedCompanyId
            ? memberships.find((m: any) => m.company_id === savedCompanyId)
            : null;

          const activeMembership = savedMembership || memberships[0];
          setCurrentCompany(activeMembership?.companies || null);
        }
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const switchCompany = (company: any) => {
    setCurrentCompany(company);
    if (user && typeof window !== 'undefined') {
      localStorage.setItem(`mrp_active_company_${user.id}`, company.id);
    }
  };

  const refreshCompany = async () => {
    if (user) await loadUserContext(user.id);
  };

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || ''
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setCurrentCompany(null);
    setUserProfile(null);
    // Clear the tab-alive flag so next open requires fresh login
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(SESSION_ALIVE_KEY);
    }
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    currentCompany,
    userProfile,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    switchCompany,
    refreshCompany,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
